import React from "react";
import useReveal from "../hooks/useReveal";
import { Star, Quote, ExternalLink, PenLine } from "lucide-react";

// Google-Profil-Links — Place-ID kann später für tieferes Linking gesetzt werden.
// Aktuell führt ein Suchlink zuverlässig auf das Google-Maps-Profil.
const GOOGLE_PLACE_ID = ""; // z. B. "ChIJ..." — sobald bekannt, hier eintragen
const GOOGLE_PROFILE_URL = GOOGLE_PLACE_ID
  ? `https://www.google.com/maps/place/?q=place_id:${GOOGLE_PLACE_ID}`
  : "https://www.google.com/maps/search/?api=1&query=Autohaus+Barwinski+by+Spitzer+Demirci+GmbH+Normannenstra%C3%9Fe+22+Neuss";
const GOOGLE_REVIEW_URL = GOOGLE_PLACE_ID
  ? `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`
  : GOOGLE_PROFILE_URL;

const reviews = [
  {
    name: "Olga Junge",
    text: "Sehr freundliches, zuverlässiges Team! Professionelle und gute Beratung — ehrlich und schnell. Was will man mehr!",
    source: "Google",
    rating: 5,
    time: "vor 6 Monaten",
  },
  {
    name: "Kunde (golocal)",
    text: "Sehr guter Service. Hatte Benzingeruch im Auto — kurz untersucht und der Fehler war innerhalb von 30 Minuten behoben. Günstiger als erwartet.",
    source: "golocal",
    rating: 5,
    time: "Rezension aus golocal",
  },
  {
    name: "Langjähriger Kunde",
    text: "Es ist nicht die erste Generation Barwinski. Warum hält sich ein solcher Betrieb so lange? Ganz einfach: ein ehrlicher Laden.",
    source: "Yelp",
    rating: 5,
    time: "Langzeit-Kunde",
  },
  {
    name: "M. Keller",
    text: "Termin bekommen, Auto pünktlich fertig, Rechnung wie besprochen. So stelle ich mir eine Werkstatt vor.",
    source: "Google",
    rating: 5,
    time: "vor 2 Monaten",
  },
  {
    name: "S. Weber",
    text: "Super freundlich am Telefon und in der Werkstatt. Diagnose war korrekt, Preis fair. Komme gerne wieder.",
    source: "11880.com",
    rating: 4,
    time: "kürzlich",
  },
  {
    name: "D. Özdemir",
    text: "TÜV bestanden, alles ordentlich erklärt. Kein Druck, keine Abzocke. Genau so sollte es sein.",
    source: "Google",
    rating: 5,
    time: "vor 3 Monaten",
  },
];

function ReviewCard({ r }) {
  return (
    <article
      data-testid={`review-card-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
      className="shrink-0 w-[320px] md:w-[420px] bg-[#0f0f12] border border-zinc-800 p-6 md:p-8 hover:border-[#E0B33D]/60 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={14}
              className={
                i < r.rating ? "fill-[#E0B33D] gold-text" : "text-zinc-700"
              }
            />
          ))}
        </div>
        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
          {r.source}
        </span>
      </div>
      <Quote size={22} className="gold-text mt-5" />
      <p className="mt-3 text-zinc-200 text-sm md:text-[15px] leading-relaxed">
        {r.text}
      </p>
      <div className="mt-6 pt-4 border-t border-zinc-800 flex items-center justify-between">
        <div className="font-display uppercase tracking-tight text-sm">
          {r.name}
        </div>
        <div className="text-[10px] text-zinc-500 tracking-wider uppercase">
          {r.time}
        </div>
      </div>
    </article>
  );
}

export default function Reviews() {
  const [ref, visible] = useReveal();
  const track = [...reviews, ...reviews];
  return (
    <section
      id="rezensionen"
      ref={ref}
      data-testid="reviews-section"
      className={`relative bg-[#09090b] border-t border-zinc-900 overflow-hidden transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase gold-text mb-4">
              — Rezensionen
            </div>
            <h2
              data-testid="reviews-heading"
              className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-3xl"
            >
              Was unsere
              <br />
              <span className="gold-text">Kunden sagen.</span>
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:items-end">
            <div className="flex items-center gap-4 border border-zinc-800 p-4 bg-[#0f0f12]">
              <div className="font-display text-5xl leading-none gold-text">
                4,7
              </div>
              <div>
                <div className="flex gap-0.5 mb-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={
                        i < 5
                          ? "fill-[#E0B33D] gold-text"
                          : "text-zinc-700"
                      }
                    />
                  ))}
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">
                  119 Google Rezensionen
                </div>
              </div>
            </div>

            {/* Google CTA Buttons */}
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <a
                href={GOOGLE_PROFILE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="google-view-link"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#E0B33D] hover:gold-text text-zinc-300 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors"
              >
                <ExternalLink size={14} /> Auf Google ansehen
              </a>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="google-review-cta"
                className="inline-flex items-center gap-2 gold-bg text-black font-semibold px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase"
              >
                <PenLine size={14} /> Bewertung schreiben
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div
        data-testid="reviews-marquee"
        className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]"
      >
        <div className="flex gap-5 w-max marquee-track pb-20 pr-5 pl-5">
          {track.map((r, i) => (
            <ReviewCard key={i} r={r} />
          ))}
        </div>
      </div>
    </section>
  );
}
